import React, { useState, useEffect, useRef } from 'react';
import { FaPlus, FaUserAlt, FaKey } from 'react-icons/fa';
import api from '../api';
import '../styles/ProfileSelector.css';
import { v4 as uuidv4 } from 'uuid';

const ProfileSelector = ({ selectedProfileId, setSelectedProfileId, onProfileChange }) => {
  const [profiles, setProfiles] = useState([]);
  const [isCreatingProfile, setIsCreatingProfile] = useState(false);
  const [newProfileName, setNewProfileName] = useState('');
  const [newProfileUuid, setNewProfileUuid] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [errors, setErrors] = useState({});
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    fetchProfiles();
  }, []);

  useEffect(() => {
    if (profiles.length > 0 && !selectedProfileId) {
      setSelectedProfileId(profiles[0].uuid);
      onProfileChange && onProfileChange(profiles[0].uuid);
    }
  }, [profiles, selectedProfileId, setSelectedProfileId, onProfileChange]);

  // Handle click outside of dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }

    // Add event listener when dropdown is open
    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    
    // Clean up the event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  const fetchProfiles = async () => {
    try {
      const response = await api.get('/api/profiles/');
      setProfiles(response.data);
    } catch (error) {
      console.error('Error fetching profiles:', error);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newProfileName.trim()) {
      newErrors.name = 'Profile name is required';
    }
    if (!newProfileUuid.trim()) {
      newErrors.uuid = 'Agent key is required';
    } else if (!/^[0-9a-f]{128}$/i.test(newProfileUuid.trim())) {
      newErrors.uuid = 'Invalid agent key format - must be 128 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateProfile = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const response = await api.post('/api/profiles/', {
        name: newProfileName,
        uuid: newProfileUuid.trim()
      });
      
      // Add the new agent profile to the list
      const newProfile = response.data;
      setProfiles([...profiles, newProfile]);
      setNewProfileName('');
      setNewProfileUuid('');
      setErrors({});
      setIsCreatingProfile(false);
      
      // Set the selected profile and trigger profile change
      setSelectedProfileId(newProfile.uuid);
      onProfileChange && onProfileChange(newProfile.uuid);
    } catch (error) {
      console.error('Error creating profile:', error);
      if (error.response?.data?.error) {
        // Handle specific error types returned from the API
        const errorMessage = error.response.data.error;
        if (errorMessage.includes("Profile with this name already exists")) {
          setErrors({ name: errorMessage });
        } else if (errorMessage.includes("agent key is already in use")) {
          setErrors({ uuid: errorMessage });
        } else {
          setErrors({ uuid: errorMessage });
        }
      } else {
        setErrors({ general: 'Failed to create profile. Please try again.' });
      }
    }
  };

  const handleSelectProfile = (profileUuid) => {
    setSelectedProfileId(profileUuid);
    onProfileChange && onProfileChange(profileUuid);
    setShowDropdown(false);
  };

  const getSelectedProfileName = () => {
    const profile = profiles.find(p => p.uuid === selectedProfileId);
    return profile ? profile.name : 'Select Profile';
  };

  // Add custom styles for the scrollable dropdown
  const scrollableListStyles = {
    maxHeight: '250px',
    overflowY: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: '#ddd #f5f5f5',
  };

  return (
    <div className="profile-selector">
      <div className="profile-selector-dropdown">
        <button 
          className="profile-selector-button" 
          onClick={() => setShowDropdown(!showDropdown)}
          ref={buttonRef}
        >
          <FaUserAlt />
          <span>{getSelectedProfileName()}</span>
        </button>
        
        {showDropdown && (
          <div className="profile-dropdown-menu" ref={dropdownRef}>
            <div className="profiles-list" style={scrollableListStyles}>
              {profiles.map((profile) => (
                <div 
                  key={profile.uuid} 
                  className={`profile-option ${selectedProfileId === profile.uuid ? 'selected' : ''}`}
                  onClick={() => handleSelectProfile(profile.uuid)}
                >
                  {profile.name}
                </div>
              ))}
            </div>
            
            {!isCreatingProfile ? (
              <div 
                className="profile-option new-profile" 
                onClick={() => setIsCreatingProfile(true)}
              >
                <FaPlus /> New Agent Profile
              </div>
            ) : (
              <form onSubmit={handleCreateProfile} className="new-profile-form">
                <div className="form-group">
                  <label htmlFor="profile-name">Profile Name</label>
                  <div className="input-group">
                    <span className="input-icon">
                      {/* Your icon here, e.g. <FaUser /> */}
                    </span>
                    <input
                      id="profile-name"
                      type="text"
                      value={newProfileName}
                      onChange={(e) => setNewProfileName(e.target.value)}
                      placeholder="Enter profile name"
                      className={errors.name ? 'error' : ''}
                      autoFocus
                    />
                  </div>
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="profile-uuid">AGENT Key</label>
                  <div className="input-group">
                    <span className="input-icon">
                      <FaKey />
                    </span>
                    <input
                      id="profile-uuid"
                      type="text"
                      value={newProfileUuid}
                      onChange={(e) => setNewProfileUuid(e.target.value)}
                      placeholder="Enter key received from the agent"
                      className={errors.uuid ? 'error' : ''}
                    />
                  </div>
                  {errors.uuid && <span className="error-message">{errors.uuid}</span>}
                </div>
                <div className="form-buttons">
                  <button type="submit">Create</button>
                  <button 
                    type="button" 
                    onClick={() => {
                      setIsCreatingProfile(false);
                      setNewProfileName('');
                      setNewProfileUuid('');
                      setErrors({});
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileSelector; 